import {
  APP_INITIALIZER,
  ApplicationConfig,
  ErrorHandler,
  provideBrowserGlobalErrorListeners
} from '@angular/core';
import * as Sentry from '@sentry/angular';
import {
  PreloadAllModules,
  provideRouter,
  Router,
  withComponentInputBinding,
  withPreloading,
  withViewTransitions,
} from '@angular/router';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { provideHttpClient } from '@angular/common/http';
import { provideTranslateService } from '@ngx-translate/core';
import { provideTranslateHttpLoader } from '@ngx-translate/http-loader';
import { TitleStrategy } from '@angular/router';
import { CustomTitleStrategy } from './core/i18n/custom-title.strategy';
import { providePrimeNG } from 'primeng/config';
import Aura from '@primeuix/themes/aura';
import { definePreset } from '@primeuix/themes';
import { appRoutes } from './app.routes';
import { provideFirebaseApp, initializeApp } from '@angular/fire/app';
import { provideAuth, getAuth } from '@angular/fire/auth';
import {
  provideFirestore,
  initializeFirestore,
  persistentLocalCache,
  persistentMultipleTabManager,
} from '@angular/fire/firestore';
import { getApp } from '@angular/fire/app';
import { provideStorage, getStorage } from '@angular/fire/storage';
import { provideFunctions, getFunctions } from '@angular/fire/functions';
import {
  provideAnalytics,
  getAnalytics,
  ScreenTrackingService,
  UserTrackingService,
} from '@angular/fire/analytics';
import { provideOurMemoryAppCheck } from './core/app-check/app-check.provider';
import { environment } from '../environments/environment';

// Skip Analytics init on localhost to avoid dev noise in GA4 dashboards.
const isLocalhost =
  typeof window !== 'undefined' &&
  /^(localhost|127\.0\.0\.1|\[::1\])$/.test(window.location.hostname);
const analyticsEnabled =
  !isLocalhost && !!(environment.firebase as { measurementId?: string }).measurementId;

const OurMemoryPreset = definePreset(Aura, {
  semantic: {
    primary: {
      50: '#fdf2ef',
      100: '#fae0d8',
      200: '#f5c1b1',
      300: '#f0a28a',
      400: '#f8ae99',
      500: '#e07d61',
      600: '#d06d52',
      700: '#c85c43',
      800: '#b04d35',
      900: '#983e27',
      950: '#7a2f19'
    }
  }
});

export const appConfig: ApplicationConfig = {
  providers: [
    provideBrowserGlobalErrorListeners(),
    provideRouter(appRoutes, withViewTransitions(), withComponentInputBinding(), withPreloading(PreloadAllModules)),
    provideAnimationsAsync(),
    provideHttpClient(),
    provideTranslateService({
      fallbackLang: 'es'
    }),
    provideTranslateHttpLoader({
      prefix: './i18n/',
      suffix: '.json'
    }),
    { provide: TitleStrategy, useClass: CustomTitleStrategy },
    providePrimeNG({
      theme: {
        preset: OurMemoryPreset,
        options: {
          darkModeSelector: '.dark',
        },
      },
    }),
    provideFirebaseApp(() => initializeApp(environment.firebase)),
    provideAuth(() => getAuth()),
    provideFirestore(() =>
      initializeFirestore(getApp(), {
        localCache: persistentLocalCache({
          tabManager: persistentMultipleTabManager(),
        }),
      })
    ),
    provideStorage(() => {
      const storage = getStorage();
      // Extend retry window for slow mobile networks (default 2 min → 5 min).
      // Avoids storage/retry-limit-exceeded errors on 3G/4G with large photos.
      storage.maxUploadRetryTime = 5 * 60 * 1000;
      storage.maxOperationRetryTime = 2 * 60 * 1000;
      return storage;
    }),
    provideFunctions(() => getFunctions()),
    ...(analyticsEnabled
      ? [
          provideAnalytics(() => getAnalytics()),
          ScreenTrackingService,
          UserTrackingService,
        ]
      : []),
    provideOurMemoryAppCheck(),
    {
      provide: ErrorHandler,
      useValue: Sentry.createErrorHandler(),
    },
    {
      provide: Sentry.TraceService,
      deps: [Router],
    },
    {
      provide: APP_INITIALIZER,
      // eslint-disable-next-line @typescript-eslint/no-empty-function
      useFactory: () => () => {},
      deps: [Sentry.TraceService],
      multi: true,
    },
  ],
};
